import axios from "axios";
import React from "react";
import {
  MdAdd,
  MdCheckCircle,
  MdDescription,
  MdHistory,
  MdOutlineEditNote,
  MdPublish,
  MdWarningAmber,
} from "react-icons/md";
import { getRequestHeaders } from "../api";
import { baseUrl } from "../assets/Animations";
import "./teacher-agreements.css";

const SCHOOLS = [
  { key: "mma", code: "MMA", name: "Mountainside", state: "Colorado" },
  { key: "lsma", code: "LSMA", name: "Lakeside", state: "Minnesota" },
  { key: "tma", code: "TMA", name: "Treeside", state: "Michigan" },
  { key: "mor", code: "MORMA", name: "Morningside", state: "Oregon" },
  { key: "psma", code: "PSMA", name: "Parkside", state: "Washington" },
];

const typeLabel = (type) =>
  type === "teacher_policies" ? "Teacher Policies" : "Non-Compete Agreement";

const readableDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? ""
    : date.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
};

const apiError = (error, fallback) =>
  error?.response?.data?.message || error?.response?.data?.error || fallback;

const TeacherAgreementAdmin = ({ userData }) => {
  const [schoolKey, setSchoolKey] = React.useState("mma");
  const [documents, setDocuments] = React.useState([]);
  const [selectedId, setSelectedId] = React.useState("");
  const [draft, setDraft] = React.useState(null);
  const [compliance, setCompliance] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [message, setMessage] = React.useState(null);
  const [publishOpen, setPublishOpen] = React.useState(false);
  const [reviewConfirmed, setReviewConfirmed] = React.useState(false);

  const loadDocuments = React.useCallback(async (preferredId = "") => {
    setLoading(true);
    setMessage(null);
    try {
      const response = await axios.get(
        `${baseUrl}/teacher-agreements/admin/documents`,
        getRequestHeaders()
      );
      const nextDocuments = response.data?.documents || [];
      setDocuments(nextDocuments);
      if (preferredId) setSelectedId(preferredId);
    } catch (error) {
      setMessage({ type: "error", text: apiError(error, "Unable to load agreements.") });
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCompliance = React.useCallback(async () => {
    try {
      const response = await axios.get(
        `${baseUrl}/teacher-agreements/admin/compliance?school=${schoolKey}`,
        getRequestHeaders()
      );
      setCompliance(response.data);
    } catch (error) {
      setCompliance({
        error: apiError(error, "Unable to load signature status."),
        teachers: [],
        documents: [],
      });
    }
  }, [schoolKey]);

  React.useEffect(() => {
    if (userData?.IsAdmin === true) loadDocuments();
  }, [loadDocuments, userData?.IsAdmin]);

  React.useEffect(() => {
    if (userData?.IsAdmin === true && !loading) loadCompliance();
  }, [loading, loadCompliance, userData?.IsAdmin]);

  const schoolDocuments = React.useMemo(
    () => documents.filter((document) => document.schoolKey === schoolKey),
    [documents, schoolKey]
  );

  React.useEffect(() => {
    const selectedStillVisible = schoolDocuments.some(
      (document) => document.id === selectedId
    );
    if (!selectedStillVisible) {
      const firstDraft = schoolDocuments.find((document) => document.status === "draft");
      setSelectedId((firstDraft || schoolDocuments[0])?.id || "");
    }
  }, [schoolDocuments, selectedId]);

  const selectedDocument = React.useMemo(
    () => documents.find((document) => document.id === selectedId) || null,
    [documents, selectedId]
  );

  React.useEffect(() => {
    setDraft(selectedDocument ? JSON.parse(JSON.stringify(selectedDocument)) : null);
    setMessage(null);
  }, [selectedDocument]);

  const updateSection = (index, field, value) => {
    setDraft((current) => ({
      ...current,
      sections: current.sections.map((section, sectionIndex) =>
        sectionIndex === index ? { ...section, [field]: value } : section
      ),
    }));
  };

  const updateSigner = (field, value) => {
    setDraft((current) => ({
      ...current,
      authorizedSigner: { ...current.authorizedSigner, [field]: value },
    }));
  };

  const saveDraft = async () => {
    if (!draft || draft.status !== "draft") return;
    setSaving(true);
    setMessage(null);
    try {
      const response = await axios.put(
        `${baseUrl}/teacher-agreements/admin/documents/${draft.id}`,
        {
          title: draft.title,
          sections: draft.sections,
          authorizedSigner: draft.authorizedSigner,
          changeSummary: draft.changeSummary,
        },
        getRequestHeaders()
      );
      const saved = response.data.document;
      setDocuments((current) =>
        current.map((document) => (document.id === saved.id ? saved : document))
      );
      setDraft(saved);
      setMessage({ type: "success", text: "Draft saved." });
    } catch (error) {
      setMessage({ type: "error", text: apiError(error, "Unable to save the draft.") });
    } finally {
      setSaving(false);
    }
  };

  const publishDraft = async () => {
    if (!draft || !reviewConfirmed) return;
    setSaving(true);
    setMessage(null);
    try {
      await axios.post(
        `${baseUrl}/teacher-agreements/admin/documents/${draft.id}/publish`,
        {
          confirmation: "PUBLISH_TEACHER_AGREEMENT",
          reviewConfirmed: true,
        },
        getRequestHeaders()
      );
      setPublishOpen(false);
      setReviewConfirmed(false);
      await loadDocuments(draft.id);
      await loadCompliance();
      setMessage({
        type: "success",
        text: "Version published. Teachers at this school can now review and sign it.",
      });
    } catch (error) {
      setMessage({ type: "error", text: apiError(error, "Unable to publish the document.") });
    } finally {
      setSaving(false);
    }
  };

  const createNewVersion = async () => {
    if (!draft) return;
    setSaving(true);
    setMessage(null);
    try {
      const response = await axios.post(
        `${baseUrl}/teacher-agreements/admin/documents/${draft.id}/new-version`,
        {},
        getRequestHeaders()
      );
      const created = response.data.document;
      await loadDocuments(created.id);
      setMessage({ type: "success", text: `Version ${created.version} draft created.` });
    } catch (error) {
      const existing = error?.response?.data?.document;
      if (existing?.id) setSelectedId(existing.id);
      setMessage({ type: "error", text: apiError(error, "Unable to create a new version.") });
    } finally {
      setSaving(false);
    }
  };

  if (userData?.IsAdmin !== true) return null;

  return (
    <div className="teacher-agreements-admin">
      <header className="teacher-agreements-admin__header">
        <div>
          <div className="teacher-agreements-admin__eyebrow">INTERNAL DOCUMENTS</div>
          <h1>Teacher Agreements &amp; Policies</h1>
          <p>Edit drafts, publish immutable versions, and see who has signed.</p>
        </div>
        <div className="teacher-agreements-admin__security-note">
          <MdHistory /> Signed versions never change
        </div>
      </header>

      <div className="teacher-agreements-admin__school-tabs" role="tablist">
        {SCHOOLS.map((school) => (
          <button
            type="button"
            key={school.key}
            className={schoolKey === school.key ? "is-active" : ""}
            onClick={() => setSchoolKey(school.key)}
          >
            <strong>{school.name}</strong>
            <span>{school.code} · {school.state}</span>
          </button>
        ))}
      </div>

      {message && (
        <div className={`teacher-agreements-admin__message is-${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="teacher-agreements-admin__workspace">
        <aside className="teacher-agreements-admin__document-list">
          <div className="teacher-agreements-admin__panel-title">Documents</div>
          {loading && <div className="teacher-agreements-admin__muted">Loading drafts…</div>}
          {!loading && schoolDocuments.map((document) => (
            <button
              type="button"
              key={document.id}
              onClick={() => setSelectedId(document.id)}
              className={`teacher-agreements-admin__document-button ${
                selectedId === document.id ? "is-selected" : ""
              }`}
            >
              <MdDescription />
              <span>
                <strong>{typeLabel(document.documentType)}</strong>
                <small>Version {document.version}</small>
              </span>
              <em className={`is-${document.status}`}>{document.status}</em>
            </button>
          ))}
        </aside>

        <main className="teacher-agreements-admin__editor">
          {!draft && !loading && (
            <div className="teacher-agreements-admin__empty">No document selected.</div>
          )}
          {draft && (
            <>
              <div className="teacher-agreements-admin__editor-heading">
                <div>
                  <span className={`teacher-agreements-admin__status is-${draft.status}`}>
                    {draft.status}
                  </span>
                  <span>Version {draft.version}</span>
                  {draft.publishedAt && <span>Published {readableDate(draft.publishedAt)}</span>}
                </div>
                {draft.status === "draft" ? (
                  <MdOutlineEditNote aria-label="Editable draft" />
                ) : (
                  <MdCheckCircle aria-label="Published version" />
                )}
              </div>

              {draft.reviewNotice && (
                <div className="teacher-agreements-admin__review-warning">
                  <MdWarningAmber />
                  <div>
                    <strong>Review before publishing</strong>
                    <p>{draft.reviewNotice}</p>
                  </div>
                </div>
              )}

              <label className="teacher-agreements-admin__field">
                <span>Document title</span>
                <input
                  value={draft.title}
                  disabled={draft.status !== "draft"}
                  onChange={(event) => setDraft({ ...draft, title: event.target.value })}
                />
              </label>

              <div className="teacher-agreements-admin__sections">
                {draft.sections.map((section, index) => (
                  <section key={section.sectionId} className="teacher-agreements-admin__section">
                    {draft.documentType === "teacher_policies" && (
                      <div className="teacher-agreements-admin__section-top">
                        <input
                          aria-label={`Section ${index + 1} title`}
                          value={section.title}
                          disabled={draft.status !== "draft"}
                          onChange={(event) => updateSection(index, "title", event.target.value)}
                        />
                        <label>
                          <input
                            type="checkbox"
                            checked={section.requiresInitials}
                            disabled={draft.status !== "draft"}
                            onChange={(event) =>
                              updateSection(index, "requiresInitials", event.target.checked)
                            }
                          />
                          Teacher initials
                        </label>
                      </div>
                    )}
                    <textarea
                      aria-label={`Section ${index + 1} text`}
                      value={section.body}
                      disabled={draft.status !== "draft"}
                      onChange={(event) => updateSection(index, "body", event.target.value)}
                    />
                  </section>
                ))}
              </div>

              <section className="teacher-agreements-admin__signature-card">
                <div>
                  <strong>Authorized company signature</strong>
                  <p>This signature is frozen into the document when it is published.</p>
                </div>
                <div className="teacher-agreements-admin__signature-grid">
                  <label>
                    <span>Name</span>
                    <input
                      value={draft.authorizedSigner?.name || ""}
                      disabled={draft.status !== "draft"}
                      onChange={(event) => updateSigner("name", event.target.value)}
                    />
                  </label>
                  <label>
                    <span>Title</span>
                    <input
                      value={draft.authorizedSigner?.title || ""}
                      disabled={draft.status !== "draft"}
                      onChange={(event) => updateSigner("title", event.target.value)}
                    />
                  </label>
                  <label className="is-signature">
                    <span>Signature</span>
                    <input
                      value={draft.authorizedSigner?.signatureText || ""}
                      disabled={draft.status !== "draft"}
                      onChange={(event) => updateSigner("signatureText", event.target.value)}
                    />
                  </label>
                </div>
              </section>

              {draft.status === "draft" && (
                <label className="teacher-agreements-admin__field">
                  <span>Change summary (optional)</span>
                  <input
                    value={draft.changeSummary || ""}
                    placeholder="What changed in this version?"
                    onChange={(event) =>
                      setDraft({ ...draft, changeSummary: event.target.value })
                    }
                  />
                </label>
              )}

              <div className="teacher-agreements-admin__actions">
                {draft.status === "draft" ? (
                  <>
                    <button type="button" className="is-secondary" onClick={saveDraft} disabled={saving}>
                      {saving ? "Saving…" : "Save Draft"}
                    </button>
                    <button
                      type="button"
                      className="is-publish"
                      onClick={() => setPublishOpen(true)}
                      disabled={saving}
                    >
                      <MdPublish /> Publish Version
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className="is-secondary"
                    onClick={createNewVersion}
                    disabled={saving}
                  >
                    <MdAdd /> Create New Version
                  </button>
                )}
              </div>
            </>
          )}
        </main>
      </div>

      <section className="teacher-agreements-admin__compliance">
        <div className="teacher-agreements-admin__compliance-header">
          <div>
            <div className="teacher-agreements-admin__panel-title">Teacher signature status</div>
            <p>Only the latest published version of each document is shown.</p>
          </div>
          <button type="button" onClick={loadCompliance}>Refresh</button>
        </div>
        {compliance?.error && (
          <div className="teacher-agreements-admin__message is-error">{compliance.error}</div>
        )}
        {!compliance?.error && compliance?.documents?.length === 0 && (
          <div className="teacher-agreements-admin__empty">
            Publish a reviewed document to begin collecting signatures.
          </div>
        )}
        {compliance?.documents?.length > 0 && (
          <div className="teacher-agreements-admin__table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Teacher</th>
                  {compliance.documents.map((document) => (
                    <th key={document.id}>{typeLabel(document.documentType)} · v{document.version}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {compliance.teachers.map((teacher) => (
                  <tr key={teacher.teacherId}>
                    <td>
                      <strong>{teacher.name}</strong>
                      <span>{teacher.email}</span>
                    </td>
                    {teacher.documents.map((document) => (
                      <td key={document.documentId}>
                        <span className={`teacher-agreements-admin__signature-status is-${document.status}`}>
                          {document.status === "signed" ? "Signed" : "Awaiting signature"}
                        </span>
                        {document.signature?.signedAt && <small>{readableDate(document.signature.signedAt)}</small>}
                        {document.previousSignatures?.length > 0 && (
                          <small className="teacher-agreements-admin__previous-signatures">
                            Previously signed {document.previousSignatures.map(
                              (signature) => `v${signature.documentVersion}`
                            ).join(", ")}
                          </small>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {publishOpen && draft && (
        <div className="teacher-agreements-admin__modal-cover" role="presentation">
          <div className="teacher-agreements-admin__publish-modal" role="dialog" aria-modal="true">
            <div className="teacher-agreements-admin__publish-icon"><MdWarningAmber /></div>
            <h2>Publish version {draft.version}?</h2>
            <p>
              Teachers at {draft.schoolName} will be asked to sign this exact version.
              It cannot be edited after publication.
            </p>
            <label className="teacher-agreements-admin__confirm-row">
              <input
                type="checkbox"
                checked={reviewConfirmed}
                onChange={(event) => setReviewConfirmed(event.target.checked)}
              />
              <span>I confirm this exact document has been reviewed and is ready for teachers.</span>
            </label>
            <div className="teacher-agreements-admin__modal-actions">
              <button
                type="button"
                className="is-secondary"
                onClick={() => {
                  setPublishOpen(false);
                  setReviewConfirmed(false);
                }}
                disabled={saving}
              >
                Keep as Draft
              </button>
              <button
                type="button"
                className="is-publish"
                onClick={publishDraft}
                disabled={!reviewConfirmed || saving}
              >
                {saving ? "Publishing…" : "Publish for Signature"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeacherAgreementAdmin;
